import { Mail } from "lucide-react";
import { type FormEvent, useState } from "react";
import { Link } from "react-router-dom";
import { api, jsonBody } from "../../api/client";
import { useRefreshAfterWrite } from "../../api/refresh";
import { useAuth } from "../../auth/AuthProvider";
import { membershipAllows } from "../../auth/permissions";
import { titleCase } from "../../lib/format";
import {
  Card,
  CardHeader,
  DataTable,
  EmptyState,
  ErrorPanel,
  Field,
  FormActions,
  Notice,
  PageHeader,
  StatusPill,
} from "../../ui/components";
import { SkeletonTable } from "../../ui/skeletons";
import { emailTemplatesKey, useEmailTemplates } from "./queries";

// Wording lives with the mailbox rather than with the mail driver. Reading and authoring are
// split the same way the server splits them: anyone who writes to customers can see the wording
// they will be sending, while changing what the organisation says under its own name stays with
// an administrator.
export function TemplatesPage() {
  const auth = useAuth();
  const canAuthor = membershipAllows(auth.session?.selectedMembership, "integrations.manage");
  // The same query the composer and the automation editor already read. One shape and one cache
  // entry, so wording saved here is the wording offered there without a second definition of a
  // template drifting away from the first.
  const templates = useEmailTemplates();
  // Not templates.refetch(): a save can land while this screen's first read is still in the air,
  // and that read would answer the refresh with the list as it was before the save.
  const refreshTemplates = useRefreshAfterWrite();
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<unknown>();

  async function saveTemplate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    setWorking(true);
    setError(undefined);
    try {
      await api("/api/v1/messaging/templates", {
        method: "POST",
        body: jsonBody({
          key: String(form.get("key")),
          subject: String(form.get("subject")),
          textBody: String(form.get("textBody")),
          htmlBody: String(form.get("htmlBody")),
          category: String(form.get("category")),
          active: form.get("active") === "on",
        }),
      });
      formElement.reset();
      await refreshTemplates(emailTemplatesKey);
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  return (
    <>
      <PageHeader
        title="Email templates"
        description="Reusable wording for the messages this workspace sends about bookings, payments, and account security."
        actions={
          <Link className="button secondary" to="/emails">
            <Mail size={17} aria-hidden="true" /> Back to emails
          </Link>
        }
      />
      {error || templates.error ? <ErrorPanel error={error ?? templates.error} /> : null}
      {canAuthor ? (
        <Card>
          <CardHeader
            title="Add or replace a template"
            description="Saving an existing key creates a new version. Older versions stay recorded."
          />
          <form className="form-grid" onSubmit={saveTemplate}>
            <Field label="Internal template name">
              <input name="key" required placeholder="booking.confirmed" />
            </Field>
            <Field label="Category">
              <select name="category" defaultValue="transactional">
                <option value="transactional">Transactional</option>
                <option value="marketing">Marketing</option>
                <option value="security">Security</option>
              </select>
            </Field>
            <Field label="Subject">
              <input name="subject" required />
            </Field>
            <label className="check-field">
              <input name="active" type="checkbox" defaultChecked /> Use this wording now
            </label>
            <Field label="Plain-text body">
              <textarea name="textBody" required />
            </Field>
            <Field label="HTML body">
              <textarea name="htmlBody" />
            </Field>
            <div className="full">
              <FormActions submitting={working} submitLabel="Save template" />
            </div>
          </form>
        </Card>
      ) : (
        <Notice tone="info">
          You can see the wording that will be sent. Changing it is an administrator task.
        </Notice>
      )}
      <Card>
        <CardHeader title="Templates in use" />
        {templates.isLoading ? (
          <SkeletonTable columns={5} rows={4} bare />
        ) : templates.data?.items.length ? (
          <DataTable
            caption="Email templates"
            items={templates.data.items}
            getKey={(item) => item.id}
            columns={[
              { label: "Key", render: (item) => <span className="mono">{item.key}</span> },
              { label: "Subject", render: (item) => item.subject },
              { label: "Category", render: (item) => titleCase(item.category) },
              { label: "Version", render: (item) => item.version },
              {
                label: "State",
                render: (item) => <StatusPill value={item.active ? "active" : "inactive"} />,
              },
            ]}
          />
        ) : (
          <EmptyState
            title="No custom templates"
            description="Add your organization's wording when a built-in email needs to sound different."
          />
        )}
      </Card>
    </>
  );
}
